/**
 * Booking Step Configuration
 * Centralized step titles, descriptions, and navigation rules.
 *
 * Flow shape (a separate `confirmation` step was removed — it only repeated the
 * summary sidebar, costing the prospect an extra click before committing):
 *
 *   default mode:    select-person → select-slot → booking-details → success
 *   event-type mode: select-slot → booking-details → success   (person is
 *                    resolved by round-robin, so that step is skipped)
 */

import { BookingStep } from './bookingConstants.js';

export interface StepInfo {
  stepNumber: number;
  totalSteps: number;
  title: string;
  description: string;
}

/** Step order per mode. Index + 1 is the displayed step number. */
const EVENT_TYPE_SEQUENCE: readonly BookingStep[] = [
  'select-slot',
  'booking-details',
  'success',
];

const DEFAULT_SEQUENCE: readonly BookingStep[] = [
  'select-person',
  'select-slot',
  'booking-details',
  'success',
];

/** The ordered steps for the current mode. */
export function getStepSequence(eventType: string | null): readonly BookingStep[] {
  return eventType ? EVENT_TYPE_SEQUENCE : DEFAULT_SEQUENCE;
}

/** Progress-indicator labels, one per entry of {@link getStepSequence}. */
export function getStepLabels(eventType: string | null): string[] {
  return eventType
    ? ['Select a time', 'Your details', 'Done']
    : ['Team member', 'Select a time', 'Your details', 'Done'];
}

/**
 * Get step information based on current step and event type
 */
export function getStepInfo(
  currentStep: BookingStep,
  eventType: string | null,
  eventTypeName: string | null
): StepInfo {
  const sequence = getStepSequence(eventType);
  const index = sequence.indexOf(currentStep);
  // A step outside the current mode's sequence (e.g. `select-person` while an
  // event type is active) reads as the first step rather than step 0.
  const stepNumber = index >= 0 ? index + 1 : 1;

  // Step titles are intentionally generic — the meeting name already lives in
  // the summary sidebar, so repeating it here would duplicate information.
  const titles: Record<BookingStep, string> = {
    'select-person': 'Select Team Member',
    'select-slot': 'Select a Time',
    'booking-details': 'Your Details',
    'success': 'Booking Confirmed!',
  };

  const descriptions: Record<BookingStep, string> = {
    'select-person': 'Choose who you want to meet with',
    'select-slot': eventType ? 'Choose your preferred time slot' : 'Select an available time',
    'booking-details': 'Review your time and confirm',
    'success': 'Your meeting has been scheduled',
  };

  return {
    stepNumber,
    totalSteps: sequence.length,
    title: titles[currentStep],
    description: descriptions[currentStep],
  };
}

/**
 * Determine if user can navigate forward from current step
 */
export function canNavigateForward(
  currentStep: BookingStep,
  bookingData: {
    bookeeEmail: string;
    selectedSlot: any;
    email: string;
    name: string;
  },
  eventType: string | null
): boolean {
  switch (currentStep) {
    case 'select-person':
      // Skip person selection if eventType is present
      return eventType ? true : !!bookingData.bookeeEmail;
    case 'select-slot':
      return !!bookingData.selectedSlot;
    case 'booking-details':
      // Details is now also the commit step, so forward means "submit" and
      // requires the contact fields the booking API demands.
      return !!bookingData.email && !!bookingData.name;
    default:
      return false;
  }
}

/**
 * Determine if user can navigate backward from current step
 */
export function canNavigateBackward(currentStep: BookingStep, eventType: string | null): boolean {
  if (currentStep === 'select-person') return false;
  if (currentStep === 'select-slot' && eventType) return false;
  if (currentStep === 'success') return false;
  return true;
}

/**
 * Get the next step after current step
 */
export function getNextStep(currentStep: BookingStep): BookingStep | null {
  const stepSequence: Record<BookingStep, BookingStep | null> = {
    'select-person': 'select-slot',
    'select-slot': 'booking-details',
    'booking-details': 'success',
    'success': null,
  };
  return stepSequence[currentStep];
}

/**
 * Get the previous step before current step
 */
export function getPreviousStep(currentStep: BookingStep, eventType: string | null): BookingStep | null {
  if (currentStep === 'select-person') return null;
  if (currentStep === 'select-slot' && eventType) return null;

  const stepSequence: Record<BookingStep, BookingStep | null> = {
    'select-person': null,
    'select-slot': 'select-person',
    'booking-details': 'select-slot',
    'success': null,
  };
  return stepSequence[currentStep];
}
